import { useCallback, useEffect, useLayoutEffect, useRef, useState } from 'react';
import type { RecordedSet, SessionExercise, Side } from '../types';
import type { SessionTimer } from '../hooks/useSessionTimer';
import { HlsVideoPlayer } from './HlsVideoPlayer';
import { EmbedVideoPlayer } from './EmbedVideoPlayer';
import { WorkoutProgressBar } from './WorkoutProgressBar';
import { formatTime } from '../utils';

function useAvailableHeight(ref: React.RefObject<HTMLDivElement | null>, padding = 16) {
  const [height, setHeight] = useState<number | undefined>(undefined);

  useLayoutEffect(() => {
    function measure() {
      if (!ref.current) return;
      const top = ref.current.getBoundingClientRect().top;
      setHeight(window.innerHeight - top - padding);
    }
    measure();
    window.addEventListener('resize', measure);
    return () => window.removeEventListener('resize', measure);
  }, [ref, padding]);

  return height;
}

interface ExercisePhaseProps {
  exercise: SessionExercise;
  nextExercise: SessionExercise | null;
  roundNumber: number;
  totalRounds: number;
  setNumber: number;
  totalSets: number;
  currentSide: Side | null;
  timer: SessionTimer;
  availableWeights?: number[];
  progressPercentage?: number;
  onComplete: (set: RecordedSet) => void;
  onFinishEarly: () => void;
  onExerciseDataChange?: (data: { reps: number; weight: number }) => void;
}

const WEIGHT_STEP = 2;

function useWeightCycler(availableWeights: number[] | undefined, initialWeight: number) {
  const sorted = availableWeights && availableWeights.length > 0
    ? [...availableWeights].sort((a, b) => a - b)
    : null;

  const cycleUp = (current: number): number => {
    if (!sorted) return current + WEIGHT_STEP;
    const idx = sorted.indexOf(current);
    const higher = sorted[idx + 1];
    if (idx >= 0 && higher !== undefined) return higher;
    const next = sorted.find(w => w > current);
    return next ?? current;
  };

  const cycleDown = (current: number): number => {
    if (!sorted) return Math.max(0, current - WEIGHT_STEP);
    const idx = sorted.indexOf(current);
    const lower = sorted[idx - 1];
    if (idx > 0 && lower !== undefined) return lower;
    const prev = [...sorted].reverse().find(w => w < current);
    return prev ?? current;
  };

  const snappedInitial =
    sorted && sorted[0] !== undefined
      ? sorted.reduce(
          (closest, w) =>
            Math.abs(w - initialWeight) < Math.abs(closest - initialWeight) ? w : closest,
          sorted[0],
        )
      : initialWeight;

  return { cycleUp, cycleDown, sortedWeights: sorted, snappedInitial };
}

// SVG icon components for +/- buttons
function MinusIcon({ className = 'h-9 w-9' }: { className?: string }) {
  return (
    <svg className={className} fill="currentColor" viewBox="0 0 24 24">
      <path fillRule="evenodd" d="M12 2.25c-5.385 0-9.75 4.365-9.75 9.75s4.365 9.75 9.75 9.75 9.75-4.365 9.75-9.75S17.385 2.25 12 2.25ZM8.25 12a.75.75 0 0 1 .75-.75h6a.75.75 0 0 1 0 1.5H9a.75.75 0 0 1-.75-.75Z" clipRule="evenodd" />
    </svg>
  );
}

function PlusIcon({ className = 'h-9 w-9' }: { className?: string }) {
  return (
    <svg className={className} fill="currentColor" viewBox="0 0 24 24">
      <path fillRule="evenodd" d="M12 2.25c-5.385 0-9.75 4.365-9.75 9.75s4.365 9.75 9.75 9.75 9.75-4.365 9.75-9.75S17.385 2.25 12 2.25ZM12.75 9a.75.75 0 0 0-1.5 0v2.25H9a.75.75 0 0 0 0 1.5h2.25V15a.75.75 0 0 0 1.5 0v-2.25H15a.75.75 0 0 0 0-1.5h-2.25V9Z" clipRule="evenodd" />
    </svg>
  );
}

export function ExercisePhase({
  exercise,
  nextExercise,
  roundNumber,
  totalRounds,
  setNumber,
  totalSets,
  currentSide,
  timer,
  availableWeights,
  progressPercentage,
  onComplete,
  onFinishEarly,
  onExerciseDataChange,
}: ExercisePhaseProps) {
  const containerRef = useRef<HTMLDivElement>(null);
  const availableHeight = useAvailableHeight(containerRef);
  const { cycleUp, cycleDown, snappedInitial } = useWeightCycler(
    availableWeights,
    exercise.suggestedWeightKg ?? 0
  );
  const [reps, setReps] = useState(exercise.targetReps ?? 0);
  const [weight, setWeight] = useState(snappedInitial);
  const timerStartedRef = useRef(false);

  useEffect(() => {
    setReps(exercise.targetReps ?? 0);
    setWeight(snappedInitial);
    timerStartedRef.current = false;
  }, [exercise.id, currentSide, snappedInitial]);

  useEffect(() => {
    if (timerStartedRef.current) return;
    timerStartedRef.current = true;

    const multiplier = exercise.timeMultiplier ?? 1;
    if (exercise.exerciseMode === 'TimeBased' && exercise.targetDurationSeconds) {
      timer.start(Math.round(exercise.targetDurationSeconds * multiplier));
    } else if (exercise.exerciseMode === 'RepBased' && exercise.targetReps) {
      const secsPerRep = exercise.timingSecondsPerRep ?? 3;
      timer.start(Math.round(exercise.targetReps * secsPerRep * multiplier));
    }
  }, [exercise, timer, currentSide]);

  useEffect(() => {
    onExerciseDataChange?.({ reps, weight });
  }, [reps, weight, onExerciseDataChange]);

  const handleComplete = useCallback(() => {
    timer.stop();
    const elapsed = timer.totalDuration - timer.timeRemaining;
    onComplete({
      sessionExerciseId: exercise.id,
      roundNumber,
      setNumber,
      repsCompleted: exercise.exerciseMode === 'RepBased' ? reps : null,
      weightUsedKg: weight > 0 ? weight : null,
      durationCompletedSeconds: exercise.exerciseMode === 'TimeBased' ? Math.round(elapsed) : null,
      side: currentSide,
      exerciseId: exercise.exerciseId,
      exerciseName: exercise.exerciseName,
      targetReps: exercise.targetReps,
    });
  }, [timer, exercise, roundNumber, setNumber, reps, weight, currentSide, onComplete]);

  const hasVideo = !!(exercise.embedUrl || exercise.videoUrl);

  // Use embed player (correctly signed) — scaled up to fill container and crop black borders
  const videoElement = exercise.embedUrl ? (
    <EmbedVideoPlayer
      src={exercise.embedUrl}
      className="h-full w-full overflow-hidden"
      isPaused={timer.isPaused}
    />
  ) : exercise.videoUrl ? (
    <HlsVideoPlayer
      src={exercise.videoUrl}
      className="h-full w-full object-cover"
      isPaused={timer.isPaused}
    />
  ) : null;

  const sideLabel = currentSide === 'left'
    ? 'Left Side'
    : currentSide === 'right'
      ? 'Right Side'
      : null;

  return (
    <div
      ref={containerRef}
      className="relative flex flex-col w-full max-w-5xl mx-auto overflow-hidden rounded-lg border border-gray-200 dark:border-dark-border bg-white dark:bg-[#111111] shadow-sm"
      style={{ height: availableHeight ? `${availableHeight}px` : 'calc(100dvh - 6rem)' }}
      data-testid="workout-exercise-phase"
    >
      {/* Video background — fills container with bottom offset to push content up */}
      {hasVideo && (
        <div className="absolute inset-0" style={{ bottom: '80px' }}>
          {videoElement}
        </div>
      )}

      {/* Header — overlaid on video */}
      <div className="relative z-10 animate-fade-in">
        <div className="flex items-start justify-between px-4 pb-3 pt-4">
          <div>
            <h2
              className="text-3xl font-semibold text-gray-900 dark:text-gray-100"
              data-testid="workout-current-exercise-name"
            >
              {exercise.exerciseName}
            </h2>
            <p
              className="text-base text-gray-500 dark:text-gray-400"
              data-testid="workout-round-indicator"
            >
              Round {roundNumber} of {totalRounds}
              {totalSets > 1 && (
                <span className="ml-1.5 text-sm" data-testid="workout-set-indicator">· Set {setNumber}/{totalSets}</span>
              )}
              {(exercise.lateralityType === 'UnilateralSequential' || exercise.lateralityType === 'UnilateralAlternating') && (
                <span className="ml-1.5 text-sm">· each side</span>
              )}
            </p>
          </div>

          {sideLabel && (
            <div
              className="rounded-lg bg-brand-600 px-3 py-1.5 text-base font-bold uppercase tracking-wider text-white shadow-sm"
              data-testid="workout-side-indicator"
            >
              {sideLabel}
            </div>
          )}
        </div>
        {progressPercentage != null && (
          <WorkoutProgressBar percentage={progressPercentage} />
        )}
      </div>

      {/* Spacer — pushes controls to bottom, video shows through */}
      <div className="flex-1" />

      {/* Controls — overlaid on video */}
      <div className="relative z-10 animate-slide-up px-4 pb-5 pt-4">
        {/* Weight / Timer / Reps — each in its own box */}
        <div className="flex items-stretch justify-center gap-3 w-full">
          {/* Weight control */}
          <div className="flex flex-1 flex-col items-center rounded-lg bg-gray-100/70 dark:bg-gray-900/50 backdrop-blur-sm border border-gray-200/50 dark:border-gray-700/50 px-3 py-3 min-w-0">
            <span className="mb-1 text-sm font-medium uppercase tracking-wide text-gray-500 dark:text-gray-400">Weight</span>
            <div className="flex items-center gap-2" data-testid="workout-weight-control">
              <button
                onClick={() => setWeight(w => cycleDown(w))}
                className="text-gray-400 dark:text-gray-500 transition-colors active:text-gray-600 dark:active:text-gray-300"
                data-testid="workout-weight-down-button"
                aria-label="Decrease weight"
              >
                <MinusIcon />
              </button>
              <span
                className="min-w-[4rem] text-center text-3xl font-bold tabular-nums text-gray-900 dark:text-gray-100"
                data-testid="workout-weight-display"
              >
                {weight}<span className="ml-0.5 text-lg font-normal text-gray-400 dark:text-gray-500">kg</span>
              </span>
              <button
                onClick={() => setWeight(w => cycleUp(w))}
                className="text-brand-600 dark:text-brand-400 transition-colors active:text-brand-700 dark:active:text-brand-300"
                data-testid="workout-weight-up-button"
                aria-label="Increase weight"
              >
                <PlusIcon />
              </button>
            </div>
          </div>

          {/* Timer */}
          <button
            onClick={() => timer.togglePause()}
            className="flex flex-1 flex-col items-center justify-center rounded-lg bg-gray-100/70 dark:bg-gray-900/50 backdrop-blur-sm border border-gray-200/50 dark:border-gray-700/50 px-3 py-3 min-w-0"
            data-testid="workout-timer-pause-button"
            aria-label={timer.isPaused ? 'Resume timer' : 'Pause timer'}
          >
            <span className="mb-0.5 text-sm font-medium uppercase tracking-wide text-gray-500 dark:text-gray-400">Time</span>
            <span
              className="text-4xl font-mono font-bold tabular-nums text-gray-900 dark:text-gray-100"
              data-testid="workout-rep-timer"
            >
              {formatTime(timer.timeRemaining)}
            </span>
            {timer.isPaused && (
              <span className="mt-0.5 text-xs font-medium text-amber-600 dark:text-amber-400">PAUSED</span>
            )}
          </button>

          {/* Reps control */}
          {exercise.exerciseMode === 'RepBased' && (
            <div className="flex flex-1 flex-col items-center rounded-lg bg-gray-100/70 dark:bg-gray-900/50 backdrop-blur-sm border border-gray-200/50 dark:border-gray-700/50 px-3 py-3 min-w-0">
              <span className="mb-1 text-sm font-medium uppercase tracking-wide text-gray-500 dark:text-gray-400">Reps</span>
              <div className="flex items-center gap-2">
                <button
                  onClick={() => setReps(r => Math.max(0, r - 1))}
                  className="text-gray-400 dark:text-gray-500 transition-colors active:text-gray-600 dark:active:text-gray-300"
                  data-testid="workout-reps-down-button"
                  aria-label="Decrease reps"
                >
                  <MinusIcon />
                </button>
                <span
                  className="min-w-[3rem] text-center text-3xl font-bold tabular-nums text-gray-900 dark:text-gray-100"
                  data-testid="workout-reps-display"
                >
                  {reps}
                </span>
                <button
                  onClick={() => setReps(r => r + 1)}
                  className="text-brand-600 dark:text-brand-400 transition-colors active:text-brand-700 dark:active:text-brand-300"
                  data-testid="workout-reps-up-button"
                  aria-label="Increase reps"
                >
                  <PlusIcon />
                </button>
              </div>
            </div>
          )}
        </div>

        {/* Next exercise preview */}
        {nextExercise && (
          <p
            className="mt-3 text-center text-lg text-gray-400 dark:text-gray-500"
            data-testid="workout-next-exercise-preview"
          >
            Next: {nextExercise.exerciseName}
            {nextExercise.exerciseMode === 'RepBased'
              ? ` · ${nextExercise.targetReps} reps`
              : ` · ${nextExercise.targetDurationSeconds}s`}
          </p>
        )}

        {/* Action buttons */}
        <div className="mt-3 flex w-full gap-3">
          <button
            onClick={onFinishEarly}
            className="flex-1 rounded-lg border border-gray-200 dark:border-dark-border px-4 py-3 text-base font-medium text-gray-600 dark:text-gray-400 transition-colors active:bg-gray-50 dark:active:bg-gray-800"
            data-testid="workout-finish-early-button"
          >
            I'm Done Early
          </button>
          <button
            onClick={handleComplete}
            className="flex-1 rounded-lg bg-brand-600 px-4 py-3 text-base font-semibold text-white shadow-sm transition-colors active:bg-brand-700"
            data-testid="workout-complete-set-button"
          >
            Done ✓
          </button>
        </div>
      </div>
    </div>
  );
}
